import React, { useMemo } from 'react';
import { Dropdown, MenuProps } from 'antd';
import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { useIntl } from 'umi';
import ContactAvatar from '@/components/Contacts/ContactAvatar';
import { ContactModel } from '@/typings/models/Contact';
import { getContactName, splitVisibleContacts, CONTACT_ENTITY } from '@/utils/contacts';
import { F, getIntlTranslation, INTL_ACTION } from '@/utils/intl';
import ContactTag, { ContactTagActions } from './ContactTag';
import styles from './index.module.less';

type ContactTagBarProps = ContactTagActions & {
  contacts: ContactModel[];
  selectedId?: string | null;
  primaryId?: string | null;
  onSelect: (contact: ContactModel) => void;
  onAdd?: () => void;
  /** The add flow is open: the add button reads as active. */
  adding?: boolean;
  /** The contact being added, shown at the end of the bar. */
  pendingTag?: React.ReactNode;
};

const ContactTagBar: React.FC<ContactTagBarProps> = ({
  contacts,
  selectedId,
  primaryId,
  onSelect,
  onAdd,
  adding,
  pendingTag,
  ...actions
}) => {
  const intl = useIntl();
  const { visible, overflow } = useMemo(
    () => splitVisibleContacts(contacts, selectedId),
    [contacts, selectedId],
  );

  const overflowMenu = useMemo<MenuProps>(
    () => ({
      items: overflow.map((contact) => ({
        key: contact.id,
        label: (
          <span className={styles.overflowItem}>
            <ContactAvatar contact={contact} />
            <span className={styles.overflowName}>{getContactName(contact)}</span>
            {contact.role && <span className={styles.overflowRole}>{contact.role}</span>}
          </span>
        ),
      })),
      onClick: ({ key }) => {
        const contact = overflow.find((item) => item.id === key);
        if (contact) onSelect(contact);
      },
    }),
    [overflow, onSelect],
  );

  const addLabel = getIntlTranslation(intl, { entity: CONTACT_ENTITY, action: INTL_ACTION.ADD });
  const compactAdd = contacts.length > 1 && !adding;

  return (
    <div className={styles.tagBar}>
      {visible.map((contact) => (
        <ContactTag
          key={contact.id}
          contact={contact}
          selected={!adding && !pendingTag && contact.id === selectedId}
          primary={contact.id === primaryId}
          onClick={() => onSelect(contact)}
          {...actions}
        />
      ))}
      {overflow.length > 0 && (
        <Dropdown menu={overflowMenu} trigger={['click']}>
          <button type="button" className={classNames(styles.tag, styles.overflowButton)}>
            +{overflow.length} <DownOutlined />
          </button>
        </Dropdown>
      )}
      {pendingTag}
      {onAdd && !pendingTag && (
        <button
          type="button"
          className={classNames(styles.addButton, {
            [styles.addButtonCompact]: compactAdd,
            [styles.addButtonActive]: adding,
          })}
          aria-label={addLabel}
          title={compactAdd ? addLabel : undefined}
          onClick={onAdd}
        >
          <PlusOutlined />
          {!compactAdd && <F entity={CONTACT_ENTITY} action={INTL_ACTION.ADD} />}
        </button>
      )}
    </div>
  );
};

export default React.memo(ContactTagBar);
